import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { 
  Gamepad2, 
  Trophy, 
  TrendingUp, 
  Clock,
  Target,
  Crown,
  ArrowRight,
  Shield
} from 'lucide-react';
import { usersAPI, gamesAPI, adminAPI, leaderboardAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // SECURITY: Admin upgrade functionality removed
  // Admin privileges must be granted by existing admins only

  const { isLoading: statsLoading } = useQuery(
    'userStats',
    () => usersAPI.getStats().then(res => res.data),
    { enabled: !!user }
  );

  const { data: activeGames, isLoading: gamesLoading } = useQuery(
    'activeGames',
    () => gamesAPI.getActiveGames().then(res => res.data.games),
    { enabled: !!user }
  );

  const { data: recentGames, isLoading: recentLoading } = useQuery(
    'recentGames',
    () => gamesAPI.getGameHistory({ limit: 5 }).then(res => res.data.games),
    { enabled: !!user }
  );

  // Get user's game stats from new leaderboard API
  const { data: userGameStats, isLoading: gameStatsLoading } = useQuery(
    'userGameStats',
    () => leaderboardAPI.getPlayerGlobalStats(user?.discordId).then(res => res.data),
    { enabled: !!user?.discordId }
  );

  if (statsLoading || gamesLoading || recentLoading || gameStatsLoading) {
    return <LoadingSpinner size="xl" />;
  }

  const getGameEmoji = (type) => {
    switch (type) {
      case 'tic-tac-toe': return '❌⭕';
      case 'number-guess': return '🔢';
      case 'rock-paper-scissors': return '🪨📄✂️';
      default: return '🎮';
    }
  };

  const getGameName = (type) => {
    switch (type) {
      case 'tic-tac-toe': return 'Tic-Tac-Toe';
      case 'number-guess': return 'Number Guess';
      case 'rock-paper-scissors': return 'Rock Paper Scissors';
      default: return 'Unknown Game';
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-discord-lightest mb-2">
              Welcome back, {user?.username}! 👋
            </h1>
            <p className="text-discord-light">
              Ready to play some games? Check out your stats and continue your gaming journey.
            </p>
          </div>
          
          <div className="flex space-x-2">
            {user?.subscription?.type === 'free' && (
              <Link
                to="/subscription"
                className="btn btn-primary flex items-center space-x-2"
              >
                <Crown className="h-4 w-4" />
                <span>Upgrade to Premium</span>
              </Link>
            )}
            
            {/* SECURITY: Admin upgrade button removed */}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-discord-blurple/20 rounded-full">
              <Gamepad2 className="h-6 w-6 text-discord-blurple" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-discord-light">Games Today</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {user?.gameStats?.gamesToday || 0}
                {user?.subscription?.type === 'free' && (
                  <span className="text-sm text-discord-light">/10</span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-discord-green/20 rounded-full">
              <Trophy className="h-6 w-6 text-discord-green" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-discord-light">Total Games</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {user?.gameStats?.totalGamesPlayed || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-discord-yellow/20 rounded-full">
              <Target className="h-6 w-6 text-discord-yellow" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-discord-light">Win Rate</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {user?.gameStats?.winRate || 0}%
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-discord-fuchsia/20 rounded-full">
              <TrendingUp className="h-6 w-6 text-discord-fuchsia" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-discord-light">Favorite Game</p>
              <p className="text-lg font-bold text-discord-lightest">
                {user?.gameStats?.favoriteGame ? getGameName(user.gameStats.favoriteGame) : 'None yet'}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-discord-yellow/20 rounded-full">
              <Trophy className="h-6 w-6 text-discord-yellow" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-discord-light">Total Points</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {userGameStats?.stats?.reduce((sum, stat) => sum + (stat.totalPoints || 0), 0) || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-xl font-bold text-discord-lightest mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/games"
              className="flex items-center justify-between p-4 bg-discord-dark rounded-lg hover:bg-discord-dark/80 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Gamepad2 className="h-5 w-5 text-discord-blurple" />
                <span className="text-discord-lightest">Play Games</span>
              </div>
              <ArrowRight className="h-4 w-4 text-discord-light" />
            </Link>
            
            <Link
              to="/leaderboard"
              className="flex items-center justify-between p-4 bg-discord-dark rounded-lg hover:bg-discord-dark/80 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Trophy className="h-5 w-5 text-discord-yellow" />
                <span className="text-discord-lightest">View Leaderboard</span>
              </div>
              <ArrowRight className="h-4 w-4 text-discord-light" />
            </Link>
            
            <Link
              to="/profile"
              className="flex items-center justify-between p-4 bg-discord-dark rounded-lg hover:bg-discord-dark/80 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Target className="h-5 w-5 text-discord-green" />
                <span className="text-discord-lightest">View Profile</span>
              </div>
              <ArrowRight className="h-4 w-4 text-discord-light" />
            </Link>
          </div>
        </div>

        {/* Active Games */}
        <div className="card">
          <h2 className="text-xl font-bold text-discord-lightest mb-4">Active Games</h2>
          {activeGames && activeGames.length > 0 ? (
            <div className="space-y-3">
              {activeGames.slice(0, 3).map((game) => (
                <div key={game.gameId} className="flex items-center justify-between p-3 bg-discord-dark rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getGameEmoji(game.type)}</span>
                    <div>
                      <p className="text-discord-lightest font-medium">
                        {getGameName(game.type)}
                      </p>
                      <p className="text-sm text-discord-light">
                        {game.status === 'waiting' ? 'Waiting for players' : 'In progress'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/games/${game.gameId}`}
                    className="text-discord-blurple hover:text-discord-blurple/80"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Gamepad2 className="h-12 w-12 text-discord-light mx-auto mb-3" />
              <p className="text-discord-light mb-4">No active games</p>
              <Link to="/games" className="btn btn-primary">
                Start Playing
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Recent Games */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-discord-lightest">Recent Games</h2>
          <Link to="/profile" className="text-discord-blurple hover:text-discord-blurple/80 text-sm">
            View All
          </Link>
        </div>
        
        {recentGames && recentGames.length > 0 ? (
          <div className="space-y-3">
            {recentGames.map((game) => (
              <div key={game.gameId} className="flex items-center justify-between p-4 bg-discord-dark rounded-lg">
                <div className="flex items-center space-x-4">
                  <span className="text-3xl">{getGameEmoji(game.type)}</span>
                  <div>
                    <p className="text-discord-lightest font-medium">
                      {getGameName(game.type)}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-discord-light">
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {game.duration ? `${game.duration}s` : 'N/A'}
                      </span>
                      <span>
                        {game.completedAt ? new Date(game.completedAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  {game.winner ? (
                    <div className="flex items-center space-x-2">
                      {game.winner.userId === user?._id ? (
                        <>
                          <Trophy className="h-4 w-4 text-discord-yellow" />
                          <span className="text-discord-green font-medium">Won</span>
                        </>
                      ) : (
                        <>
                          <Target className="h-4 w-4 text-discord-light" />
                          <span className="text-discord-light">Lost</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <span className="text-discord-light">Draw</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Gamepad2 className="h-12 w-12 text-discord-light mx-auto mb-3" />
            <p className="text-discord-light mb-4">No recent games</p>
            <Link to="/games" className="btn btn-primary">
              Start Your First Game
            </Link>
          </div>
        )}
      </div>

      {/* Subscription Status */}
      {user?.subscription?.type === 'free' && (
        <div className="card bg-gradient-to-r from-discord-blurple/10 to-discord-fuchsia/10 border-discord-blurple/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-discord-lightest mb-2">
                Unlock Premium Features
              </h3>
              <p className="text-discord-light">
                Get unlimited games, advanced stats, and premium support with our Premium plan.
              </p>
            </div>
            <Link to="/subscription" className="btn btn-primary">
              <Crown className="h-4 w-4 mr-2" />
              Upgrade Now
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
